/**
 * Admin Navigation Configuration
 *
 * Nav items for the Bimanova admin portal, organized into groups.
 * Each item has a `visibleTo` array controlling which platform roles can see it.
 */

import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  ShieldCheck,
  Bell,
  BarChart3,
  ArrowLeftRight,
  Receipt,
  CreditCard,
  UserCog,
  History,
  Settings,
} from 'lucide-react';
import { ROUTE_PATHS } from '@/navigation/constants';
import type { PlatformRole } from '@/core/auth/constants';
import type { LucideIcon } from 'lucide-react';

export interface AdminNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  visibleTo: PlatformRole[] | 'all';
  badge?: number;
}

export interface AdminNavGroup {
  id: string;
  label?: string; // undefined = no group header (top-level ungrouped items)
  items: AdminNavItem[];
}

const ALL: 'all' = 'all';

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: 'top',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        path: ROUTE_PATHS.ADMIN_DASHBOARD,
        visibleTo: ALL,
      },
    ],
  },
  {
    id: 'tenants',
    label: 'Tenants',
    items: [
      {
        id: 'businesses',
        label: 'Businesses',
        icon: Building2,
        path: ROUTE_PATHS.ADMIN_BUSINESSES,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_OPS', 'PLATFORM_FINANCE'],
      },
      {
        id: 'support',
        label: 'Support Desk',
        icon: MessageSquare,
        path: ROUTE_PATHS.ADMIN_SUPPORT,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_SUPPORT'],
      },
      {
        id: 'regulatory',
        label: 'Regulatory',
        icon: ShieldCheck,
        path: ROUTE_PATHS.ADMIN_REGULATORY,
        visibleTo: ['SUPER_ADMIN', 'REGULATORY_OFFICER'],
      },
      {
        id: 'notifications',
        label: 'Notifications',
        icon: Bell,
        path: ROUTE_PATHS.ADMIN_NOTIFICATIONS,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_SUPPORT'],
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        path: ROUTE_PATHS.ADMIN_ANALYTICS,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_OPS', 'PLATFORM_FINANCE'],
      },
    ],
  },
  {
    id: 'financials',
    label: 'Financials',
    items: [
      {
        id: 'transactions',
        label: 'Transactions',
        icon: ArrowLeftRight,
        path: ROUTE_PATHS.ADMIN_TRANSACTIONS,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_FINANCE'],
      },
      {
        id: 'invoices',
        label: 'Invoices',
        icon: Receipt,
        path: ROUTE_PATHS.ADMIN_INVOICES,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_FINANCE'],
      },
      {
        id: 'payments',
        label: 'Payments',
        icon: CreditCard,
        path: ROUTE_PATHS.ADMIN_PAYMENTS,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_FINANCE'],
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      {
        id: 'team',
        label: 'Team Members',
        icon: UserCog,
        path: ROUTE_PATHS.ADMIN_TEAM,
        visibleTo: ['SUPER_ADMIN'],
      },
      {
        id: 'audit-log',
        label: 'Audit Log',
        icon: History,
        path: ROUTE_PATHS.ADMIN_AUDIT_LOG,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_OPS'],
      },
      {
        id: 'config',
        label: 'System Config',
        icon: Settings,
        path: ROUTE_PATHS.ADMIN_CONFIG,
        visibleTo: ['SUPER_ADMIN', 'PLATFORM_OPS'],
      },
    ],
  },
];

